import { TableCell } from '@ovComponents/2-component/table/table';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import {
  type AllocationUnit,
  AllocationUnits,
  displayAmountCentsFromFraction,
  displayPercentToFraction,
  formatAllocationAmountDisplay,
  fractionFromAmountCents,
  fractionToDisplayPercent,
} from '@utils/allocationUnits';
import { dollars2cents } from '@utils/dollars2cents';
import { formatPercentValue } from '@utils/moneyFormatting';
import Decimal from 'decimal.js';
import { Typography, useTheme } from 'hephaestus/web';
import { useEffect, useMemo, useState } from 'react';
import {
  ALLOCATION_AMOUNT_FIELD_PROPS,
  ALLOCATION_INPUT_STYLE,
  ALLOCATION_PERCENT_FIELD_PROPS,
} from './constants';
import { DisabledAllocationCell } from './DisabledAllocationCell';
import { useCellReleaseControl } from './useCellReleaseControl';
import { getPortfolioCustomTargetColumnSx } from './utils';

const sanitizeDollarInput = (value: string) => value.replaceAll(',', '').trim();

/** Ignore sub-0.01% blur edits — avoids re-locking a cell on float noise from display rounding. */
const PERCENT_COMMIT_TOLERANCE = displayPercentToFraction('0.01');

const percentInputProps = (ariaLabel?: string, describedBy?: string) =>
  ariaLabel || describedBy
    ? { 'aria-label': ariaLabel, 'aria-describedby': describedBy }
    : undefined;

const centsToDollarInput = (cents: number): string =>
  new Decimal(cents).div(100).toString();

export type CellStateTone = { border: string; fill: string };
export type CellStateTones = {
  locked: CellStateTone;
  uploaded: CellStateTone;
};

const stateToneOf = ({
  isLocked,
  uploaded,
  stateTones,
}: {
  isLocked?: boolean;
  uploaded?: boolean;
  stateTones?: CellStateTones;
}) => {
  if (uploaded) return stateTones?.uploaded;
  if (isLocked) return stateTones?.locked;
  return undefined;
};

// NumberField sets an inline white background, so the fill needs !important.
const toneSx = ({ border, fill }: CellStateTone) => ({
  '& .MuiOutlinedInput-root': {
    borderLeft: `2px solid ${border}`,
    backgroundColor: `${fill} !important`,
    '& fieldset': {
      borderColor: border,
    },
  },
});

const getCustomTargetInputSx = ({
  inViolation,
  isLocked,
  uploaded,
  stateTones,
  primaryColor,
  negativeColor,
}: {
  inViolation?: boolean;
  isLocked?: boolean;
  uploaded?: boolean;
  stateTones?: CellStateTones;
  primaryColor?: string;
  negativeColor?: string;
}) => {
  const tone = stateToneOf({ isLocked, uploaded, stateTones });
  if (tone) return toneSx(tone);

  let color: string | undefined;
  if (isLocked) {
    color = primaryColor;
  } else if (inViolation) {
    color = negativeColor;
  }
  if (!color) return undefined;

  return {
    '& .MuiOutlinedInput-root': {
      borderLeft: `2px solid ${color}`,
      '& fieldset': {
        borderColor: color,
      },
      '& input': {
        color,
      },
    },
  };
};

export const CustomAllocationCell = ({
  unit,
  fraction,
  mvCents,
  disabled,
  readOnly,
  notHeld,
  notTradeable,
  isLocked,
  uploaded,
  inViolation,
  ariaLabel,
  releaseLabel,
  testId,
  sx,
  stateTones,
  onCommit,
  onRelease,
}: {
  unit: AllocationUnit;
  fraction: number;
  mvCents: number;
  disabled?: boolean;
  readOnly?: boolean;
  notHeld?: boolean;
  notTradeable?: boolean;
  isLocked?: boolean;
  uploaded?: boolean;
  inViolation?: boolean;
  /** Accessible name (member + security) — the visible column header covers sighted users. */
  ariaLabel?: string;
  releaseLabel?: string;
  testId?: string;
  sx?: { height?: string; textAlign?: 'left' | 'right' };
  stateTones?: CellStateTones;
  onCommit: (newFraction: number, amountCents?: number) => void;
  onRelease?: () => void;
}) => {
  const theme = useTheme();
  const cellSx = getPortfolioCustomTargetColumnSx();
  const tableCellSx = { ...cellSx, ...sx };
  const inputSx = useMemo(
    () =>
      getCustomTargetInputSx({
        inViolation,
        isLocked,
        uploaded,
        stateTones,
        primaryColor: theme.color.primary,
        negativeColor: theme.color.negative,
      }),
    [
      inViolation,
      isLocked,
      uploaded,
      stateTones,
      theme.color.primary,
      theme.color.negative,
    ],
  );
  const release = useCellReleaseControl({
    isLocked,
    uploaded,
    releaseLabel,
    onRelease,
  });
  const [isEditingPercent, setIsEditingPercent] = useState(false);
  const [percentDraft, setPercentDraft] = useState('');
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [amountDraft, setAmountDraft] = useState('');

  useEffect(() => {
    setIsEditingPercent(false);
    setPercentDraft('');
    setIsEditingAmount(false);
    setAmountDraft('');
  }, [fraction, unit]);

  if (readOnly) {
    const readOnlyValue =
      unit === AllocationUnits.DOLLAR
        ? formatAllocationAmountDisplay(
            displayAmountCentsFromFraction(fraction, mvCents),
          )
        : formatPercentValue(fraction);

    return (
      <TableCell sx={tableCellSx} data-testid={testId}>
        <Typography variant='bodySmall'>{readOnlyValue}</Typography>
      </TableCell>
    );
  }

  if (notTradeable || notHeld) {
    return (
      <DisabledAllocationCell
        unit={unit}
        fraction={fraction}
        mvCents={mvCents}
        reason={notTradeable ? 'notTradeable' : 'notHeld'}
        ariaLabel={ariaLabel}
      />
    );
  }

  if (unit === AllocationUnits.PERCENT) {
    const displayPercent = isEditingPercent
      ? percentDraft
      : `${fractionToDisplayPercent(fraction)}`;

    return (
      <TableCell sx={tableCellSx} data-testid={testId}>
        {release.withControl(
          <NumberField
            {...ALLOCATION_PERCENT_FIELD_PROPS}
            number={displayPercent}
            setNumber={setPercentDraft}
            onFocus={() => {
              setIsEditingPercent(true);
              setPercentDraft(`${fractionToDisplayPercent(fraction)}`);
            }}
            onBlur={() => {
              setIsEditingPercent(false);
              if (!percentDraft) {
                setPercentDraft('');
                return;
              }
              const nextFraction = displayPercentToFraction(percentDraft);
              if (
                Math.abs(nextFraction - fraction) <= PERCENT_COMMIT_TOLERANCE
              ) {
                setPercentDraft('');
                return;
              }
              onCommit(nextFraction);
              setPercentDraft('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
            }}
            disabled={disabled}
            sx={inputSx}
            inputProps={percentInputProps(ariaLabel, release.describedBy)}
          />,
        )}
      </TableCell>
    );
  }

  const amountCents = displayAmountCentsFromFraction(fraction, mvCents);

  const handleAmountFocus = () => {
    setIsEditingAmount(true);
    setAmountDraft(centsToDollarInput(amountCents));
  };

  const handleAmountBlur = () => {
    setIsEditingAmount(false);
    if (!amountDraft) {
      setAmountDraft('');
      return;
    }
    const committedCents = dollars2cents(sanitizeDollarInput(amountDraft));
    const nextFraction = fractionFromAmountCents(committedCents, mvCents);
    if (displayAmountCentsFromFraction(fraction, mvCents) === committedCents) {
      setAmountDraft('');
      return;
    }
    onCommit(nextFraction, committedCents);
    setAmountDraft('');
  };

  return (
    <TableCell sx={tableCellSx} data-testid={testId}>
      <AmountField
        {...ALLOCATION_AMOUNT_FIELD_PROPS}
        useFormatAmountValueCents={!isEditingAmount}
        amount={isEditingAmount ? amountDraft : `${amountCents}`}
        disabled={disabled}
        inputProps={{
          style: ALLOCATION_INPUT_STYLE,
          onFocus: handleAmountFocus,
          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          },
          ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        }}
        sx={inputSx}
        setAmount={setAmountDraft}
        onBlur={handleAmountBlur}
      />
    </TableCell>
  );
};
